import { Application, IApplicationOptions } from 'pixi.js-legacy';

import { container, IGameApp, events, userModel, device, INewHowToPlaySlideStrings, buildDynamicStringBlock } from '@game-studio/arcade-base';

import { HowToPlay } from '../how-to-play/how-to-play';
import { gameModel } from '../../models/game-model';
import { gameEventEmitter } from '@/src/events';
import Rating from '../../services/rating';
import { GameView } from './game-view';
import { gameSettings } from '../../models/game-settings';
import { calculateAspectRatioDecimal } from '../../services/aspect-ratio';

const PORTRAIT_RATIO_LIMIT: number = 1.6;
export class GameApplication implements IGameApp {
    // Application is static so it can be accessed easily for things like RenderTextures
    public static pixiApp: Application;
    public static portraitMode: boolean;

    private howToPlay: HowToPlay;
    private gameView: GameView;

    public initPixi(options: IApplicationOptions): Application {
        options.resolution = Rating.getRating();
        GameApplication.portraitMode = calculateAspectRatioDecimal() < PORTRAIT_RATIO_LIMIT ? true : false;
        GameApplication.pixiApp = new Application(options);
        return GameApplication.pixiApp;
    }

    public async preInit(): Promise<void> {
        const rtp = gameSettings.rtp;
        container.strings.howToPlay.slides[1] = buildDynamicStringBlock(container.strings.howToPlay.slides[1] as INewHowToPlaySlideStrings, { RTP: rtp + '%' });

        this.howToPlay = new HowToPlay();
        this.gameView = new GameView();

        GameApplication.pixiApp.stage.addChild(this.gameView);
        GameApplication.pixiApp.stage.addChild(container.cabinet);
        GameApplication.pixiApp.stage.addChild(container.bigWin);
        GameApplication.pixiApp.stage.addChild(this.howToPlay);

        events.POPUP.TOGGLE_HTP_POPUP.addListener(this.toggleHtpPopup, this);
    }

    public async init(): Promise<void> {
        GameApplication.pixiApp.view.addEventListener('webglcontextlost', () => {
            gameEventEmitter.emit('CONTEXT_LOST');
        });
        GameApplication.pixiApp.view.addEventListener('webglcontextrestored', () => {
            gameEventEmitter.emit('CONTEXT_RESTORED');
        });

        // Disable cabinet for intro.
        container.cabinet.disablePurchaseButton();
        container.cabinet.disableStakeButtons();

        // Start the game
        if (gameModel.isNewPlayer) {
            await this.howToPlay.show();
            if (userModel.freePlayBalance > 0) {
                events.GLOBAL.FREE_PLAY_EARNED.dispatch(userModel.freePlayBalance);
            }
        }

        //Remove blank boxes & titles from gamerules.
        const blocksToRemove: number[] = [];
        container.strings.gameRules.blocks.forEach((value, index) => {
            if (value.title === '') {
                value.content.forEach((content) => {
                    if (content.value === '') {
                        blocksToRemove.push(index);
                    }
                });
            }
        });

        // Remove blocks in reverse order to avoid index shifting
        blocksToRemove.reverse().forEach(index => {
            container.strings.gameRules.blocks.splice(index, 1);
        });

        // Begin music and intro.
        this.gameView.start();

        // GAME.BEGIN emitted by the BasePackage.Cabinet on a successful buy in
        events.GAME.BEGIN.addListener(this.onBeginGame, this);

        // container.frameConnection.showMenuItem('settings');
    }

    public enterBonusRound(): void {
        // no bonus round in this game
    }

    public resize(width: number, height: number): void {
        GameApplication.pixiApp.renderer.resize(width, height);
        container.cabinet.handleOrientationChange();
        container.bigWin.resize(width, height);
        this.howToPlay.resize(width, height);
        this.gameView.draw();

        if (device.type.isDesktop) {
            // add disclaimer spacing
            container.cabinet.y = -container.cabinet.height * 0.28;
            return;
        }

        if (device.orientation.isLandscape || GameApplication.portraitMode) {
            // add disclaimer spacing
            container.cabinet.y = -container.cabinet.height * 0.18;
            return;
        }

        container.cabinet.y = 0;
    }

    private onBeginGame(): void {
        gameEventEmitter.emit('NEW_GAME');
    }

    private toggleHtpPopup(isVisible: boolean | void = true): void {
        if (isVisible) {
            this.howToPlay.show();
        } else {
            this.howToPlay.hide();
        }
    }
}
